'use client';

// The enclosing Modal's presentation (card or phone sheet), in its own module so
// the sheet's header and intro (`ModalHead`, `SheetIntro`) can read it while
// `Modal` itself renders `ModalHead` without an import cycle (BL-2405).

import { createContext, useContext, type ReactNode } from 'react';
import { FieldSizeContext } from '@auctogon/ui/web';

/** How the enclosing Modal is presented: the centred card, or the phone sheet. */
export type ModalPresentation = 'card' | 'sheet';

/**
 * The enclosing Modal's presentation, so content can adapt to the sheet
 * (autofocus, where the help line sits, which action bar) without the caller
 * threading a second prop. `card` outside a Modal and in every Modal without
 * `phoneSheet`.
 */
export const ModalPresentationContext = createContext<ModalPresentation>('card');

/**
 * The presentation of the Modal this component renders inside.
 *
 * @returns `'sheet'` inside a `phoneSheet` Modal on a phone, otherwise `'card'`.
 */
export function useModalPresentation(): ModalPresentation {
    return useContext(ModalPresentationContext);
}

/**
 * Resets the presentation to `card` for an overlay that portals out of a
 * phone sheet (BL-2403): context flows through portals, so without this a
 * `ConfirmDialog` opened from a sheet would take the sheet's red and banners.
 *
 * @param props - The overlay's content.
 * @returns The content under a `card` presentation.
 */
export function CardPresentation({ children }: { children: ReactNode }) {
    return <ModalPresentationContext.Provider value="card">{children}</ModalPresentationContext.Provider>;
}

/**
 * The phone sheet's presentation for a subtree: presentation `sheet` and the touch field geometry
 * (BL-2108), the two contexts a `phoneSheet` Modal hands its body. `Modal` uses it for its own
 * sheet, and the lot drawer's view mode uses it on a phone (BL-2234), so a form reads the same
 * phone look wherever it sits, from one source.
 *
 * `active={false}` resets to the card presentation and the default field size with the same
 * tree, so a caller that switches on the viewport never remounts its content, and a card Modal
 * opened from inside a sheet never inherits the sheet's touch fields.
 *
 * @param props - The content, and whether the sheet look applies (default true).
 * @returns The content under a `sheet` presentation with touch fields.
 */
export function SheetPresentation({ children, active = true }: { children: ReactNode; active?: boolean }) {
    return (
        <ModalPresentationContext.Provider value={active ? 'sheet' : 'card'}>
            <FieldSizeContext.Provider value={active ? 'touch' : undefined}>{children}</FieldSizeContext.Provider>
        </ModalPresentationContext.Provider>
    );
}
